import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  MapPin,
  Users,
  Clock,
  DollarSign,
  Save,
  ArrowLeft,
  Tag,
  Upload,
  X,
  Image
} from 'lucide-react'

interface Activity {
  id: string
  name: string
  location: string
  city?: string
  country?: string
  region?: string
  category: string
  duration_hours: number
  max_participants: number
  price: number
  currency?: string
  description: string
  included_items: string[]
  requirements: string[]
  images: Array<{
    id: string
    url: string
    file?: File
    label: string
    description?: string
    isNew?: boolean
  }>
  status: 'active' | 'inactive'
  created_at: string
}

interface ActivityManagementProps {
  tenantId: string
}

// Currencies list (shared across components)
const currencies = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' }
]

const ActivityManagement: React.FC<ActivityManagementProps> = ({ tenantId }) => {
  return (
    <Routes>
      <Route path="/" element={<ActivityList tenantId={tenantId} />} />
      <Route path="/new" element={<ActivityForm tenantId={tenantId} />} />
      <Route path="/edit/:id" element={<ActivityForm tenantId={tenantId} />} />
      <Route path="/view/:id" element={<ActivityView tenantId={tenantId} />} />
    </Routes>
  )
}

// Activity List Component
const ActivityList: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')

  const categories = ['Culture', 'Adventure', 'Entertainment', 'Sightseeing', 'Food & Drink', 'Wildlife', 'Sports']

  useEffect(() => {
    fetchActivities()
  }, [tenantId])

  const fetchActivities = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/activities?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        setActivities(data.activities)
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error)
    } finally {
      setLoading(false)
    }
  }

  const deleteActivity = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/activities/${id}?tenant_id=${tenantId}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        setActivities(activities.filter(activity => activity.id !== id))
      }
    } catch (error) {
      console.error('Failed to delete activity:', error)
    }
  }

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === 'all' || activity.category === filterCategory
    const matchesStatus = filterStatus === 'all' || activity.status === filterStatus
    return matchesSearch && matchesCategory && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Management</h1>
          <p className="text-gray-600">Manage your activity offerings</p>
        </div>
        <Link
          to="/dashboard/activities/new"
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-150"
        >
          <Plus className="w-4 h-4" />
          Add Activity
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search activities by name or location..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredActivities.map((activity) => (
          <div key={activity.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Activity Image */}
            <div className="h-48 bg-gray-200 relative">
              {activity.images.length > 0 ? (
                <img
                  src={typeof activity.images[0] === 'string'
                    ? ((activity.images[0] as string).startsWith('http') ? activity.images[0] : `${API_BASE_URL}/uploads/${activity.images[0]}`)
                    : ((activity.images[0] as any).url?.startsWith('http') ? (activity.images[0] as any).url : `${API_BASE_URL}${(activity.images[0] as any).url}`)}
                  alt={activity.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <MapPin className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <div className="absolute top-2 right-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  activity.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {activity.status}
                </span>
              </div>
              <div className="absolute top-2 left-2">
                <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                  {activity.category}
                </span>
              </div>
            </div>

            {/* Activity Info */}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{activity.name}</h3>
                  <div className="flex items-center text-gray-600 text-sm mt-1">
                    <MapPin className="w-4 h-4 mr-1" />
                    {activity.location}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-3 text-sm text-gray-600">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {activity.duration_hours}h
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Max {activity.max_participants}
                </div>
              </div>

              <div className="flex items-center mt-2 text-lg font-semibold text-purple-600">
                <DollarSign className="w-5 h-5 mr-1" />
                {(activity.currency && currencies.find(c => c.code === activity.currency)?.symbol) || '$'}{activity.price}
                {activity.currency && activity.currency !== 'USD' && (
                  <span className="ml-1 text-sm text-gray-500">{activity.currency}</span>
                )}
              </div>

              <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                {activity.description}
              </p>

              {/* Actions */}
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
                <Link
                  to={`/dashboard/activities/view/${activity.id}`}
                  className="text-purple-600 hover:text-purple-700 p-1 rounded"
                >
                  <Eye className="w-4 h-4" />
                </Link>
                <Link
                  to={`/dashboard/activities/edit/${activity.id}`}
                  className="text-blue-600 hover:text-blue-700 p-1 rounded"
                >
                  <Edit className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => deleteActivity(activity.id)}
                  className="text-red-600 hover:text-red-700 p-1 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredActivities.length === 0 && (
        <div className="text-center py-12">
          <MapPin className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No activities found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterCategory !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your search or filter criteria.' 
              : 'Get started by adding your first activity.'}
          </p>
          {!searchTerm && filterCategory === 'all' && filterStatus === 'all' && (
            <div className="mt-6">
              <Link
                to="/dashboard/activities/new"
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Activity
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Activity Form Component
const ActivityForm: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [activity, setActivity] = useState({
    name: '',
    location: '',
    city: '',
    country: '',
    region: '',
    category: '',
    duration_hours: 0,
    max_participants: 0,
    price: 0,
    currency: 'USD',
    description: '',
    included_items: [] as string[],
    requirements: [] as string[],
    images: [] as any[],
    status: 'active' as 'active' | 'inactive'
  })

  // Check if we're in edit mode
  const isEditMode = window.location.pathname.includes('/edit/')
  const activityId = isEditMode ? window.location.pathname.split('/').pop() : null

  const categories = ['Culture', 'Adventure', 'Entertainment', 'Sightseeing', 'Food & Drink', 'Wildlife', 'Sports']
  
  const [newIncludedItem, setNewIncludedItem] = useState('')
  const [newRequirement, setNewRequirement] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)

  // Load activity data if in edit mode
  useEffect(() => {
    if (isEditMode && activityId) {
      fetchActivityForEdit(activityId)
    } else {
      setInitialLoading(false)
    }
  }, [isEditMode, activityId, tenantId])

  const fetchActivityForEdit = async (id: string) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/activities/${id}?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        // Ensure arrays are properly formatted and null values are converted to empty strings
        const activityData = data.activity
        setActivity({
          ...activityData,
          city: activityData.city || '',
          country: activityData.country || '',
          region: activityData.region || '',
          included_items: Array.isArray(activityData.included_items) ? activityData.included_items : [],
          requirements: Array.isArray(activityData.requirements) ? activityData.requirements : [],
          images: Array.isArray(activityData.images) ? activityData.images : []
        })
      }
    } catch (error) {
      console.error('Failed to fetch activity for edit:', error)
    } finally {
      setInitialLoading(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploadingImage(true)
    
    try {
      const uploadedImages: any[] = []
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const formData = new FormData()
        formData.append('file', file)
        
        const response = await authFetch(`${API_BASE_URL}/api/admin/activities/upload-image?tenant_id=${tenantId}`, {
          method: 'POST',
          body: formData
        })
        
        if (response.ok) {
          const result = await response.json()
          // Create proper image object with URL from backend response
          uploadedImages.push({
            id: Date.now() + i, // Simple unique ID
            url: `/uploads/images/${result.filename}`, // Use relative path to avoid double-prefixing
            label: 'Activity Image',
            description: '',
            filename: result.filename,
            isNew: true
          })
        } else {
          const errorText = await response.text()
          console.error('Failed to upload image:', file.name, 'Status:', response.status, 'Error:', errorText)
        }
      }
      
      if (uploadedImages.length > 0) {
        setActivity(prev => ({
          ...prev,
          images: [...prev.images, ...uploadedImages]
        }))
      }
    } catch (error) {
      console.error('Error uploading images:', error)
    } finally {
      setUploadingImage(false)
    }
  }

  const removeImage = (index: number) => {
    setActivity(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      let response
      
      if (isEditMode && activityId) {
        // Update existing activity with FormData for file uploads
        const formData = new FormData()
        
        // Add basic fields
        formData.append('name', activity.name)
        formData.append('location', activity.location)
        formData.append('city', activity.city || '')
        formData.append('country', activity.country || '')
        formData.append('region', activity.region || '')
        formData.append('category', activity.category)
        formData.append('duration_hours', activity.duration_hours.toString())
        formData.append('max_participants', activity.max_participants.toString())
        formData.append('price', activity.price.toString())
        formData.append('description', activity.description || '')
        formData.append('included_items', JSON.stringify(activity.included_items || []))
        formData.append('requirements', JSON.stringify(activity.requirements || []))
        formData.append('currency', activity.currency || 'USD')
        formData.append('status', activity.status || 'active')
        
        // Add image metadata (for existing images)
        const imageMetadata: any[] = []
        activity.images.forEach((img, index) => {
          if (img.file) {
            // New image file
            formData.append('images', img.file)
            imageMetadata.push({
              id: img.id,
              label: img.label || 'Activity Image',
              description: img.description || '',
              isNew: true,
              index
            })
          } else {
            // Existing image
            imageMetadata.push({
              id: img.id,
              url: img.url,
              label: img.label || 'Activity Image',
              description: img.description || '',
              isNew: false
            })
          }
        })
        
        formData.append('image_metadata', JSON.stringify(imageMetadata))

        response = await authFetch(`${API_BASE_URL}/api/admin/activities/${activityId}?tenant_id=${tenantId}`, {
          method: 'PUT',
          body: formData,
          // Don't set Content-Type - let browser set it with boundary
        })

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Activity update failed:', errorText);
        }
      } else {
        // Create new activity
        response = await authFetch(`${API_BASE_URL}/api/admin/activities?tenant_id=${tenantId}`, {
          method: 'POST',
          body: JSON.stringify(activity)
        })
      }

      if (response.ok) {
        navigate('/dashboard/activities')
      } else {
        console.error(`Failed to ${isEditMode ? 'update' : 'create'} activity`)
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} activity:`, error)
    } finally {
      setLoading(false)
    }
  }

  const addIncludedItem = () => {
    if (newIncludedItem.trim()) {
      setActivity(prev => ({
        ...prev,
        included_items: [...prev.included_items, newIncludedItem.trim()]
      }))
      setNewIncludedItem('')
    }
  }

  const removeIncludedItem = (index: number) => {
    setActivity(prev => ({
      ...prev,
      included_items: prev.included_items.filter((_, i) => i !== index)
    }))
  }

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setActivity(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }))
      setNewRequirement('')
    }
  }

  const removeRequirement = (index: number) => {
    setActivity(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }))
  }

  // Show loading spinner while fetching data for edit mode
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/activities')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Activity' : 'Add New Activity'}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? 'Update activity details' : 'Create a new activity offering'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activity Name *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.name}
                onChange={(e) => setActivity(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Eiffel Tower Skip-the-Line Tour"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.category}
                onChange={(e) => setActivity(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.location}
                onChange={(e) => setActivity(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Paris, France"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.city}
                onChange={(e) => setActivity(prev => ({ ...prev, city: e.target.value }))}
                placeholder="e.g., London"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.country}
                onChange={(e) => setActivity(prev => ({ ...prev, country: e.target.value }))}
                placeholder="e.g., UK"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Region (optional)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.region}
                onChange={(e) => setActivity(prev => ({ ...prev, region: e.target.value }))}
                placeholder="e.g., Western Europe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (hours) *
              </label>
              <input
                type="number"
                required
                min="0.5"
                step="0.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.duration_hours}
                onChange={(e) => setActivity(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) }))}
                placeholder="2.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Participants *
              </label>
              <input
                type="number"
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={activity.max_participants}
                onChange={(e) => setActivity(prev => ({ ...prev, max_participants: parseInt(e.target.value) }))}
                placeholder="20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price per Person *
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={activity.price}
                    onChange={(e) => setActivity(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                    placeholder="75.00"
                  />
                </div>
                <div className="w-24">
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={activity.currency}
                    onChange={(e) => setActivity(prev => ({ ...prev, currency: e.target.value }))}
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select currency for pricing
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={activity.description}
              onChange={(e) => setActivity(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the activity, what participants can expect, highlights..."
            />
          </div>

          {/* Included Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              What's Included
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={newIncludedItem}
                  onChange={(e) => setNewIncludedItem(e.target.value)}
                  placeholder="e.g., Professional guide"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIncludedItem())}
                />
                <button
                  type="button"
                  onClick={addIncludedItem}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {activity.included_items.map((item, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                  >
                    {item}
                    <button
                      type="button"
                      onClick={() => removeIncludedItem(index)}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Requirements
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  placeholder="e.g., Moderate fitness level required"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                />
                <button
                  type="button"
                  onClick={addRequirement}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {activity.requirements.map((req, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full"
                  >
                    {req}
                    <button
                      type="button"
                      onClick={() => removeRequirement(index)}
                      className="ml-2 text-yellow-600 hover:text-yellow-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Activity Images
            </label>
            <div className="space-y-4">
              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                <input
                  type="file"
                  id="image-upload"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <div className="space-y-2">
                    <Upload className="mx-auto w-8 h-8 text-gray-400" />
                    <div className="text-sm">
                      <span className="text-purple-600 font-medium">Click to upload images</span>
                      <span className="text-gray-500"> or drag and drop</span>
                    </div>
                    <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB each</p>
                  </div>
                </label>
              </div>

              {/* Upload Progress */}
              {uploadingImage && (
                <div className="flex items-center gap-2 text-sm text-purple-600">
                  <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                  Uploading images...
                </div>
              )}

              {/* Uploaded Images */}
              {activity.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {activity.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={typeof image === 'string'
                            ? ((image as string).startsWith('http') ? image : `${API_BASE_URL}/uploads/${image}`)
                            : ((image as any).url?.startsWith('http') ? (image as any).url : `${API_BASE_URL}${(image as any).url}`)}
                          alt={`${activity.name} - View ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* No Images State */}
              {activity.images.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <Image className="mx-auto w-8 h-8 text-gray-300 mb-2" />
                  <p className="text-sm">No images uploaded yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={activity.status}
              onChange={(e) => setActivity(prev => ({ ...prev, status: e.target.value as any }))}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/activities')}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isEditMode ? 'Update Activity' : 'Save Activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Activity View Component
const ActivityView: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const navigate = useNavigate()
  const [activity, setActivity] = useState<Activity | null>(null)
  const [loading, setLoading] = useState(true)
  const activityId = window.location.pathname.split('/').pop()

  useEffect(() => {
    if (activityId) {
      fetchActivity(activityId)
    }
  }, [activityId, tenantId])

  const fetchActivity = async (id: string) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/activities/${id}?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        // Ensure arrays are properly formatted
        const activityData = data.activity
        setActivity({
          ...activityData,
          included_items: Array.isArray(activityData.included_items) ? activityData.included_items : [],
          requirements: Array.isArray(activityData.requirements) ? activityData.requirements : [],
          images: Array.isArray(activityData.images) ? activityData.images : []
        })
      }
    } catch (error) {
      console.error('Failed to fetch activity:', error)
    } finally {
      setLoading(false)
    }
  }

  const deleteActivity = async () => {
    if (!activity || !window.confirm('Are you sure you want to delete this activity?')) return
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/activities/${activity.id}?tenant_id=${tenantId}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        navigate('/dashboard/activities')
      }
    } catch (error) {
      console.error('Failed to delete activity:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (!activity) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Activity not found</h3>
        <p className="text-gray-500 mt-2">The activity you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/dashboard/activities')}
          className="mt-4 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
        >
          Back to Activities
        </button>
      </div>
    )
  }

  const getCurrencySymbol = (currencyCode: string) => {
    const currency = currencies.find(c => c.code === currencyCode)
    return currency?.symbol || '$'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/activities')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{activity.name}</h1>
            <p className="text-gray-600">Activity Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/dashboard/activities/edit/${activity.id}`}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Link>
          <button
            onClick={deleteActivity}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Category</label>
                <div className="mt-1 flex items-center">
                  <Tag className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-gray-900">{activity.category}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Location</label>
                <div className="mt-1 flex items-center">
                  <MapPin className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-gray-900">{activity.location}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Duration</label>
                <div className="mt-1 flex items-center">
                  <Clock className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-gray-900">{activity.duration_hours} hours</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Max Participants</label>
                <div className="mt-1 flex items-center">
                  <Users className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-gray-900">{activity.max_participants} people</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Price per Person</label>
                <div className="mt-1 flex items-center">
                  <DollarSign className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-gray-900 text-lg font-semibold">
                    {getCurrencySymbol(activity.currency || 'USD')}{activity.price}
                    {activity.currency && activity.currency !== 'USD' && (
                      <span className="ml-1 text-sm text-gray-500">{activity.currency}</span>
                    )}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                  activity.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {activity.status}
                </span>
              </div>
            </div>
          </div>

          {/* Description Card */}
          {activity.description && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
              <p className="text-gray-700 leading-relaxed">{activity.description}</p>
            </div>
          )}

          {/* What's Included Card */}
          {activity.included_items && activity.included_items.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h3>
              <div className="flex flex-wrap gap-2">
                {activity.included_items.map((item, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Requirements Card */}
          {activity.requirements && activity.requirements.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h3>
              <div className="flex flex-wrap gap-2">
                {activity.requirements.map((req, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full"
                  >
                    {req}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Images Card */}
          {activity.images && activity.images.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Images</h3>
              <div className="grid grid-cols-1 gap-4">
                {activity.images.map((image, index) => (
                  <div key={index} className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={typeof image === 'string'
                        ? ((image as string).startsWith('http') ? image : `${API_BASE_URL}/uploads/${image}`)
                        : ((image as any).url?.startsWith('http') ? (image as any).url : `${API_BASE_URL}${(image as any).url}`)}
                      alt={`${activity.name} - Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(activity.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Images</span>
                <span className="text-sm font-medium text-gray-900">
                  {activity.images?.length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Inclusions</span>
                <span className="text-sm font-medium text-gray-900">
                  {activity.included_items?.length || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActivityManagement